import { isUnknownArray } from "../types/data";
import type { Rating } from "../types/rating";
import type { EntityId, PersonCredit } from "../types/media";
import {
  COMPACT_CAST_LIMIT,
  isComposerCrewMember,
  isDirectorCrewMember
} from "./compactFilmCredits";

const asArray = <T>(value: T | T[] | null | undefined): T[] => isUnknownArray(value) ? value : value ? [value] : [];
export const RATING_PROGRESS_CAST_LIMIT = COMPACT_CAST_LIMIT;

const normalizeText = (value: unknown) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

export const getPersonId = (person: PersonCredit | string | null | undefined) =>
  person && typeof person === "object"
    ? person.id ?? person.personId ?? person.person_id ?? person.tmdbId ?? person.tmdb_id
    : null;

export const getPersonNames = (person: PersonCredit | string | null | undefined): string[] => {
  if (!person) return [];
  if (typeof person === "string") return [person];
  return [
    person.name,
    person.original_name,
    person.originalName,
    person.english_name
  ].filter((name): name is string => Boolean(name));
};

export const getPersonIdentity = (person: PersonCredit | string | null | undefined) => {
  const personId = getPersonId(person);
  if (personId != null && String(personId).trim() && String(personId) !== "0") {
    return `id:${String(personId).trim()}`;
  }
  const names = getPersonNames(person).map(normalizeText).filter(Boolean);
  return names.length ? `name:${names[1] || names[0]}` : "";
};

const uniquePeople = <T extends PersonCredit | string>(sources: (T | T[] | undefined | null)[] = []): T[] => {
  const result = new Map<string, T>();
  asArray(sources).flatMap(asArray).filter(Boolean).forEach((person) => {
    const identity = getPersonIdentity(person);
    if (identity && !result.has(identity)) result.set(identity, person);
  });
  return [...result.values()];
};

const getCast = (rating: Rating = {}) => uniquePeople([
  rating.cast,
  rating.credits?.cast,
  rating.filmData?.cast,
  rating.filmData?.credits?.cast,
  rating.fullDataFilm?.cast,
  rating.fullDataFilm?.credits?.cast,
  rating.details?.cast,
  rating.details?.credits?.cast
]);

const getCrew = (rating: Rating = {}) => uniquePeople([
  rating.crew,
  rating.credits?.crew,
  rating.filmData?.crew,
  rating.filmData?.credits?.crew,
  rating.fullDataFilm?.crew,
  rating.fullDataFilm?.credits?.crew,
  rating.details?.crew,
  rating.details?.credits?.crew
]);

export const getRatingActors = (rating: Rating = {}) =>
  getCast(rating).slice(0, RATING_PROGRESS_CAST_LIMIT);

export const getRatingDirectors = (rating: Rating = {}) => uniquePeople([
  rating.directors,
  getCrew(rating).filter(isDirectorCrewMember)
]);

export const getRatingComposers = (rating: Rating = {}) => uniquePeople([
  rating.composers,
  getCrew(rating).filter(isComposerCrewMember)
]);

const tokenSetMatches = (left: string, right: string) => {
  const leftTokens = normalizeText(left).split(" ").filter(Boolean);
  const rightTokens = normalizeText(right).split(" ").filter(Boolean);
  if (leftTokens.length < 2 || leftTokens.length !== rightTokens.length) return false;
  const rightSet = new Set(rightTokens);
  return leftTokens.every((token) => rightSet.has(token));
};

export interface PersonCondition {
  personId?: EntityId | null;
  person_id?: EntityId;
  tmdbId?: EntityId;
  tmdb_id?: EntityId;
  value?: EntityId;
  aliases?: string[];
}
export const personMatchesCondition = (people: (PersonCredit | string)[] = [], condition: PersonCondition = {}) => {
  const wantedIds = [
    condition.personId,
    condition.person_id,
    condition.tmdbId,
    condition.tmdb_id
  ].map((value) => String(value || "").trim()).filter(Boolean);
  if (
    wantedIds.length &&
    asArray(people).some((person) => wantedIds.includes(String(getPersonId(person) || "")))
  ) {
    return true;
  }

  const wantedNames = [condition.value, ...(condition.aliases || [])]
    .map(normalizeText)
    .filter(Boolean);
  return asArray(people).some((person) =>
    getPersonNames(person).some((name) => {
      const normalizedName = normalizeText(name);
      return wantedNames.some((wanted) =>
        normalizedName === wanted ||
        tokenSetMatches(normalizedName, wanted)
      );
    })
  );
};
